import Loading from '@mortgage-pos/ui/components/Loading'
import Tooltip from '@mortgage-pos/ui/components/Tooltip'
import { useNavigate } from 'react-router-dom'
import rollbar from '@mortgage-pos/ui/services/rollbar'
import RateCardCompare from './RateCardCompare'
import { CompareFooter } from './CompareFooter'
import { redirectTo } from '@mortgage-pos/utils'
import React, { useState, useEffect } from 'react'
import LoanOfficerPricing from '@mortgage-pos/ui/components/LoanOfficerPricing'
import { CompareValueProps } from './CompareValueProps'
import newRelic from '@mortgage-pos/ui/services/newRelic'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'
import DownArrow from '@mortgage-pos/ui/assets/arrow-down.svg'
import { useVariation } from '@mortgage-pos/ui/services/featureFlags'
import PricingInfoAccordion from '@mortgage-pos/ui/components/PricingInfoAccordion'
import { useStoreState, useStoreActions } from '@mortgage-pos/ui/store'
import { environment } from '@mortgage-pos/ui/env'
import { WhyRefiPricingLogic } from '@mortgage-pos/ui/utils/WhyRefiPricingLogic'

import {
  Rate,
  Routes,
  Status,
  RateSelectPageVariation,
} from '@mortgage-pos/types'

import {
  LOCardVariant,
  LoanOfficerCard,
} from '@mortgage-pos/ui/components/pricing/LoanOfficerPricingCard'

interface RateSelectionProps {
  modifierClass?: string
  redirectToSlug?: string
}

const contentDefaults: RateSelectPageVariation = {
  variation: 'default',
  title: 'We can beat your current offer',
  subTitle: 'The results are in!',
  cta: 'Apply for this offer',
}

const CompareLERateSelection = ({
  modifierClass = '--compare',
  redirectToSlug = '',
}: RateSelectionProps) => {
  const navigate = useNavigate()

  const showCompareDemo = useVariation(
    FEATURE_FLAG_TYPES.ENABLE_COMPARE_LE_DEMO
  )

  const isCompareLightningTest = useVariation(
    FEATURE_FLAG_TYPES.COMPARE_LIGHTNING_TEST_ENABLED
  )

  const { isRequestingRates, sortedRates } = useStoreState(
    (state) => state.rates
  )

  const { loanOfficer } = useStoreState((state) => state.loanOfficer)
  const { ratesPage } = useStoreState((state) => state.questionnaire)
  const { mergedAnswers } = useStoreState((state) => state.answers)
  const { applicationGuid } = useStoreState((state) => state.application)
  const { leRate, rawLEData } = useStoreState((state) => state.leComparison)

  const { submitRateSelection, requestRates } = useStoreActions(
    (state) => state.rates
  )

  const { formSubmitted } = useStoreActions((state) => state.tracking)
  const { getLoanOfficer } = useStoreActions((state) => state.loanOfficer)
  const { next } = useStoreActions((state) => state.questionnaire)
  const { updateStatus } = useStoreActions((state) => state.application)
  const { elementClicked } = useStoreActions((store) => store.tracking)

  const whyRefiPricingLogic = new WhyRefiPricingLogic(
    mergedAnswers.refiReason,
    {
      sortedRates,
      remainingTermCount: rawLEData?.term,
    }
  )

  const matchedRates = whyRefiPricingLogic?.findMatchedRates()

  useEffect(() => {
    if (typeof showCompareDemo === 'boolean' && !showCompareDemo) {
      navigate(`/${Routes.APP_1003}`)
    }
  }, [navigate, showCompareDemo])

  // Use page content coming from Storyblok, with a fallback to default values
  const pageContent = ratesPage?.variations?.[0] || contentDefaults

  const [showLocards, setShowLoCards] = useState(false)

  const handleTalkToExpertClick = () => {
    newRelic.increment('ntl_rate_selection.talk_to_an_expert.click')

    setShowLoCards(true)
    updateStatus(Status.CallRequested)
  }

  const handleGoBackToRatesClick = () => {
    setShowLoCards(false)
  }

  useEffect(() => {
    async function getRates() {
      try {
        // @ts-ignore
        const ratesResponse = await requestRates()

        if (!ratesResponse.length) {
          await updateStatus(Status.QNoRatesDisplayed)
          return navigate(`/${Routes.APP_1003}/no-rates`)
        }
      } catch (e) {
        await updateStatus(Status.QNoRatesDisplayed)
        return navigate(`/${Routes.APP_1003}/no-rates`)
      }

      await updateStatus(Status.QRatesDisplayed)
    }

    getRates()
  }, [])

  useEffect(() => {
    async function getAssignedLoanOfficer() {
      await getLoanOfficer()
    }

    if (!loanOfficer) {
      getAssignedLoanOfficer()
    }

    if (process.env.PRODUCT_NAME === 'interest') {
      navigate(`/${Routes.APP_1003}`)
    }
  }, [])

  const submitRate = (rate: Rate) => async (e: React.SyntheticEvent) => {
    e.preventDefault()

    try {
      await submitRateSelection(rate)
      formSubmitted()

      if (!redirectToSlug) {
        return next()
      }

      return redirectTo(
        `${environment.questionnaireBaseUrl}/application-1003?question-set=${redirectToSlug}&application-guid=${applicationGuid}`
      )
    } catch (err) {
      rollbar.error('Failed to submit rate', { sensitive: err })
      navigate(`/${Routes.APP_1003}/error`)
    }
  }

  const handleSeeOptionsClick = (e: React.SyntheticEvent) => {
    e.preventDefault()

    elementClicked({
      elementType: 'Button',
      location: 'Sage Compare Rates Page',
      actionOutcome: 'Routes user to Alfie question set',
      text: 'See other options',
    })

    return redirectTo(
      `${environment.questionnaireBaseUrl}/application-1003?question-set=ntl&application-guid=${applicationGuid}`
    )
  }

  if (isRequestingRates || !sortedRates.length) {
    return <Loading loaderType="house" shouldWrap={false} />
  }

  const tooltipContent = {
    title: 'What we assume',
    text: `In addition to the details shown and extracted from your document,
      we use public information to identify mortgage insurance premiums, property
      taxes, property insurance and the interest rate for your property. If public
      information is not available, we use standard values in our calculations.
      Results do not account for all variables of your specific loan transaction,
      and provide a preliminary estimate for comparison purposes only. Insurance
      and taxes are not included in these estimates. This is NOT a mortgage loan
      approval or commitment to lend, and all estimates are subject to change
      without notice. Terms and conditions apply.`,
  }

  return (
    <div className={`rate-selections ${modifierClass || ''}`}>
      <h4
        className="compare-page__subtitle"
        data-testid="rate-selection-header"
      >
        {pageContent.subTitle}
      </h4>

      <h1 className="compare-page__title">
        {pageContent.title}

        {isCompareLightningTest ? (
          <Tooltip
            className="compare-page__legal-tooltip"
            title={tooltipContent.title}
            text={tooltipContent.text}
          />
        ) : null}
      </h1>

      {showLocards ? (
        <LoanOfficerPricing
          loanOfficer={loanOfficer}
          handleGoBackToRatesClick={handleGoBackToRatesClick}
        />
      ) : (
        <>
          <div className={`rate-selections__options-list --row --wrap-content`}>
            {/* Don't try to display non-existent prior rate, if it is unavailable */}
            {rawLEData ? (
              <RateCardCompare
                rate={leRate}
                superlativeName={'currentLoan'}
                modifierClass={'--other-lender'}
                lender={rawLEData?.lender || 'Other Lender'}
              />
            ) : null}

            <RateCardCompare
              rate={matchedRates.rates[0]}
              superlativeName={matchedRates.rateItems[0]}
              handleSubmitRateClick={submitRate(matchedRates.rates[0])}
              cta={pageContent.cta}
            />

            <RateCardCompare
              rate={matchedRates.rates[1]}
              superlativeName={'interest'} // TODO for BR POC only, need to fix with real rate info
              handleSubmitRateClick={submitRate(matchedRates.rates[1])}
              cta={pageContent.cta}
            />

            {/* Throw in a 3rd rate if no loan doc was uploaded or if it failed to parse */}
            {!rawLEData ? (
              <RateCardCompare
                rate={matchedRates.rates[2]}
                superlativeName={'interest'} // TODO for BR POC only, need to fix with real rate info
                handleSubmitRateClick={submitRate(matchedRates.rates[2])}
                cta={pageContent.cta}
              />
            ) : null}
          </div>
          <button
            className="compare-page__next link"
            onClick={handleSeeOptionsClick}
          >
            Continue to see more options
            <img
              className="compare-page__next-arrow"
              src={DownArrow}
              alt="Arrow down icon"
            />
          </button>

          <div className="compare-page__disclaimer-block">
            Insurance and taxes not included. These options are based on several
            assumptions and are being displayed as a preliminary estimate to you
            as a convenience for comparison purposes. Mortgage rates and loan
            options depend on a number of variables, including the accuracy of
            the information you have entered, which is subject to verification,
            credit scores, and property approval. This is NOT a mortgage loan
            approval or commitment to lend. The actual fees, costs, and monthly
            payment on your specific loan transaction may vary, and may include
            city, county, or other additional fees and costs. Get an official
            Loan Estimate before choosing a loan. Rates and fees are subject to
            change without prior notice. Terms and conditions apply
          </div>

          <CompareValueProps />
          <PricingInfoAccordion />

          {loanOfficer ? (
            <LoanOfficerCard
              variant={LOCardVariant.compare}
              heading={`Hi, I'm ${loanOfficer.name}!`}
              body={'Have any questions about your matches?'}
              cta={'Click here'}
              onCtaClick={handleTalkToExpertClick}
            />
          ) : null}
        </>
      )}

      <CompareFooter showRateInfo={true} />
    </div>
  )
}

export default CompareLERateSelection
